import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import {
  ArrowLeft, Loader2, RefreshCw, CheckCircle2, XCircle,
  Shield, ExternalLink, User as UserIcon, Settings, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import AIReviewPanel from './AIReviewPanel';

/**
 * Iter-205 — Admin IDV Review Queue.
 *
 * Pending / Rejected tabs. Approve = one click.
 * Reject = modal with canonical reason dropdown + optional admin note.
 * On submit, talent gets in-app notification with the reason + a deep
 * link back to /talent/idv to re-upload.
 */

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    // Iter-255 — Short date format for the IDV review card so a
    // narrow mobile column can render the date on a single line.
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch (_) { return iso; }
};

// Iter-247b — IDV expiration chip with red/amber/gray urgency colouring.
const ExpirationChip = ({ expiresAt, userId }) => {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  const today = new Date();
  const daysOut = Math.floor((d - today) / (1000 * 60 * 60 * 24));
  const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  let cls = 'text-gray-800';
  let suffix = '';
  if (daysOut < 0) {
    cls = 'text-rose-700 font-semibold';
    suffix = ` · EXPIRED ${Math.abs(daysOut)}d ago`;
  } else if (daysOut <= 30) {
    cls = 'text-amber-700 font-semibold';
    suffix = ` · in ${daysOut}d`;
  } else if (daysOut <= 90) {
    cls = 'text-cyan-700';
    suffix = ` · in ${daysOut}d`;
  } else {
    suffix = ` · in ${daysOut}d`;
  }
  return (
    <span className={cls} data-testid={`idv-expires-chip-${userId}`}>
      {dateStr}{suffix}
    </span>
  );
};

const IdvReviewQueue = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // AI-IDV state
  const [rerunning, setRerunning] = useState(null);          // user_id being re-run
  const [showConfig, setShowConfig] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const s = await api.get('/admin/idv/stats');
      setPendingCount(s?.data?.pending ?? 0);
      setRejectedCount(s?.data?.rejected ?? 0);
    } catch (_) { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/idv/submissions', {
        params: { status: tab, limit: 100 },
      });
      setItems(Array.isArray(res?.data?.submissions) ? res.data.submissions : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load IDV submissions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab]);

  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  useEffect(() => {
    // Iter-220a — Pull canonical reason_key/label pairs from the
    // admin-editable template manager. This is the source-of-truth
    // dropdown — surfaces any new reasons admins add via
    // `/admin/idv-rejection-templates` immediately. Falls back to the
    // legacy free-text catalogue if the templates table isn't seeded.
    api.get('/admin/idv-rejection-templates/reasons')
      .then((r) => {
        const reasons = r?.data?.reasons || [];
        // Filter out the meta `default` fallback row — it's used
        // server-side when a key isn't found, not as an admin choice.
        setRejectionReasons(reasons.filter((x) => x.reason_key !== 'default'));
      })
      .catch(() => {
        // Fallback to legacy endpoint if templates not seeded yet.
        api.get('/admin/idv/rejection-reasons')
          .then((r2) => setRejectionReasons(r2?.data?.reasons || []))
          .catch(() => setRejectionReasons([
            'Missing front', 'Missing back',
            'Blurry image', 'Expired ID',
            'Name mismatch', 'Unsupported document',
            'Cropped image', 'Document unreadable',
            'Selfie mismatch', 'Other',
          ]));
      });
  }, []);

  const approve = async (sub) => {
    try {
      await api.patch(`/admin/idv/submissions/${sub.user_id}/review`, { action: 'approve' });
      toast.success(`IDV approved for ${sub.talent_name}`);
      load();
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    }
  };

  const openReject = (sub) => {
    setRejectTarget(sub);
    setRejectReason('');
    setRejectNotes('');
  };

  const submitReject = async () => {
    if (!rejectTarget || !rejectReason) {
      toast.error('Select a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      // Iter-220a — submit the canonical reason_key so the backend
      // dispatches the admin-editable email + SMS template and logs
      // an `idv_rejection_events` row for the talent timeline.
      const selected = rejectionReasons.find(r => (r.reason_key || r) === rejectReason);
      const label = (selected && selected.label) || rejectReason;
      await api.patch(`/admin/idv/submissions/${rejectTarget.user_id}/review`, {
        action: 'reject',
        rejection_reason: label,
        rejection_reason_key: rejectReason,
        admin_notes: rejectNotes || null,
      });
      toast.success(`IDV rejected — ${rejectTarget.talent_name} notified.`);
      setRejectTarget(null);
      load();
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setSubmitting(false);
    }
  };

  const refresh = () => { setRefreshing(true); load(); loadStats(); };

  // ────────────────────────────────────────────────────────
  // AI-IDV — re-run + config
  // ────────────────────────────────────────────────────────
  const rerunAI = async (userId) => {
    setRerunning(userId);
    try {
      const r = await api.post(`/admin/idv/submissions/${userId}/rerun-ai`);
      const s = r?.data || {};
      toast.success(
        `AI re-review · ${s.confidence_score?.toFixed?.(1) ?? '—'}% · ${s.recommended_action}`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-run failed');
    } finally { setRerunning(null); }
  };

  const openConfig = async () => {
    setShowConfig(true);
    try {
      const r = await api.get('/admin/idv/ai-config');
      setAiConfig(r?.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load AI config');
    }
  };

  const saveConfig = async (patch) => {
    setSavingConfig(true);
    try {
      const r = await api.patch('/admin/idv/ai-config', patch);
      setAiConfig(r?.data || {});
      toast.success('AI settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingConfig(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link to="/admin/home" className="text-gray-500 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              ID Verification Review
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={openConfig}
              data-testid="idv-ai-config-btn"
            >
              <Sparkles className="w-4 h-4" />
              <span className="ml-1">AI settings</span>
            </Button>
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="idv-review-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1">Refresh</span>
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="idv-tab-pending">
              Pending <Badge variant="outline" className="ml-2">{pendingCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="idv-tab-rejected">
              Rejected <Badge variant="outline" className="ml-2">{rejectedCount}</Badge>
            </TabsTrigger>
          </TabsList>

          {['pending', 'rejected'].map((value) => (
            <TabsContent key={value} value={value}>
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="py-16 flex items-center justify-center text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                  ) : items.length === 0 ? (
                    <div className="py-16 text-center text-gray-500 text-sm" data-testid={`idv-${value}-empty`}>
                      No {value} IDV submissions.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {items.map((s) => (
                        <li key={s.user_id} className="p-4 sm:p-5" data-testid={`idv-row-${s.user_id}`}>
                          {/* Header row — avatar, name + email */}
                          <div className="flex items-start gap-3">
                            <Link
                              to={`/admin/talent/${s.user_id}`}
                              className="shrink-0 w-12 h-12 rounded-full bg-gray-200 overflow-hidden inline-flex items-center justify-center"
                            >
                              {s.profile_photo ? (
                                <img src={s.profile_photo} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <UserIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </Link>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Link
                                  to={`/admin/talent/${s.user_id}`}
                                  className="font-semibold text-gray-900 hover:text-cyan-700 hover:underline"
                                >
                                  {s.talent_name}
                                </Link>
                                {s.role_name && (
                                  <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    {s.role_name}
                                  </Badge>
                                )}
                                {value === 'rejected' && (
                                  <Badge className="bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                    <XCircle className="w-3 h-3" /> Rejected
                                  </Badge>
                                )}
                                {s.is_resubmitted && (
                                  <Badge
                                    className="bg-cyan-50 text-cyan-700 border border-cyan-200 inline-flex items-center gap-1"
                                    data-testid={`idv-resubmitted-badge-${s.user_id}`}
                                  >
                                    <RefreshCw className="w-3 h-3" /> Resubmitted
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5 break-all">{s.email}</div>
                            </div>
                          </div>

                          {/* Iter-255 — Info grid. 2-col on mobile, 4-col on tablet+ so dates
                              get plenty of horizontal space and no longer wrap one word per line. */}
                          <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                            <div>
                              <dt className="text-gray-500">Submitted</dt>
                              <dd className="text-gray-800 whitespace-nowrap">{formatDate(s.submitted_at)}</dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">Type</dt>
                              <dd className="text-gray-800 capitalize">
                                {(s.document_type || '—').replace(/_/g, ' ')}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">City</dt>
                              <dd className="text-gray-800">
                                {[s.city, s.state].filter(Boolean).join(', ') || '—'}
                              </dd>
                            </div>
                            <div data-testid={`idv-expiration-${s.user_id}`}>
                              <dt className="text-gray-500">ID expires</dt>
                              <dd className="whitespace-nowrap">
                                {s.no_expiration ? (
                                  <span className="text-emerald-700 font-medium">No expiration</span>
                                ) : s.expires_at ? (
                                  <ExpirationChip expiresAt={s.expires_at} userId={s.user_id} />
                                ) : (
                                  <span className="text-gray-400">Not set</span>
                                )}
                              </dd>
                            </div>
                            {value === 'rejected' && s.rejection_reason && (
                              <div className="col-span-2 sm:col-span-4">
                                <dt className="text-gray-500">Last reason</dt>
                                <dd className="text-rose-700">
                                  {s.rejection_reason}
                                  {s.admin_notes && (
                                    <span className="text-gray-500"> · note: <span className="text-gray-700 italic">{s.admin_notes}</span></span>
                                  )}
                                </dd>
                              </div>
                            )}
                            {s.is_resubmitted && s.previous_rejection_reason && (
                              <div className="col-span-2 sm:col-span-4 rounded-md bg-cyan-50 border border-cyan-200 p-2">
                                <div className="text-cyan-900 font-medium">Previously rejected for:</div>
                                <div className="text-cyan-800">{s.previous_rejection_reason}</div>
                                {s.previous_admin_notes && (
                                  <div className="text-cyan-700 italic mt-0.5">
                                    Admin note: {s.previous_admin_notes}
                                  </div>
                                )}
                              </div>
                            )}
                          </dl>

                          {/* Document thumbnails */}
                          <div className="flex items-center gap-2 mt-3 flex-wrap">
                            {['document_front_url', 'document_back_url', 'selfie_url'].map((field) => {
                              const url = s[field];
                              if (!url) return null;
                              const label =
                                field === 'document_front_url' ? 'Front'
                                : field === 'document_back_url' ? 'Back'
                                : 'Selfie';
                              return (
                                <a
                                  key={field}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-gray-200 hover:border-cyan-300 text-cyan-700"
                                  data-testid={`idv-doc-${field}-${s.user_id}`}
                                >
                                  <ExternalLink className="w-3 h-3" /> {label}
                                </a>
                              );
                            })}
                            {!s.document_front_url && !s.document_back_url && !s.selfie_url && (
                              <span className="text-[11px] text-gray-500">No documents uploaded</span>
                            )}
                          </div>

                          {/* AI-IDV — inline AI reviewer verdict */}
                          <AIReviewPanel
                            review={s.ai_review}
                            duplicateOf={null}
                            onRerun={() => rerunAI(s.user_id)}
                            rerunning={rerunning === s.user_id}
                            testIdSuffix={s.user_id}
                          />

                          {/* Iter-255 — Approve / Reject row pushed to the bottom of the
                              card and given a separator. On mobile the buttons go full-width
                              for thumb reach; on desktop they sit right-aligned. */}
                          {value === 'pending' && (
                            <div className="mt-4 pt-3 border-t border-gray-100 flex flex-col sm:flex-row sm:justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => approve(s)}
                                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`idv-approve-btn-${s.user_id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReject(s)}
                                className="w-full sm:w-auto border-rose-300 text-rose-700 hover:bg-rose-50"
                                data-testid={`idv-reject-btn-${s.user_id}`}
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) setRejectTarget(null); }}>
        <DialogContent data-testid="idv-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-600" /> Reject ID verification
            </DialogTitle>
          </DialogHeader>
          {rejectTarget && (
            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                Rejecting ID verification for
                <span className="font-semibold"> {rejectTarget.talent_name}</span>. They'll be
                notified with the reason + your note and prompted to re-upload.
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-600 uppercase tracking-wide font-medium">
                    Rejection reason <span className="text-rose-600">*</span>
                  </label>
                  {/* Iter-220c — inline deep-link so admins can edit the
                      template copy mid-review without losing context. Opens
                      the Template Manager in a new tab. */}
                  <Link
                    to="/admin/idv-rejection-templates"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-900 hover:underline"
                    data-testid="idv-reject-manage-templates-link"
                  >
                    <Settings className="w-3 h-3" /> Manage templates
                  </Link>
                </div>
                <Select value={rejectReason} onValueChange={setRejectReason}>
                  <SelectTrigger data-testid="idv-reject-reason-select">
                    <SelectValue placeholder="Choose a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {rejectionReasons.map((r) => {
                      // Backward-compat: legacy list returned strings.
                      const key = (r && r.reason_key) || r;
                      const label = (r && r.label) || r;
                      return (
                        <SelectItem key={key} value={key} data-testid={`idv-reject-reason-option-${key}`}>{label}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-600 uppercase tracking-wide font-medium">
                  Admin note <span className="text-gray-400">(optional, shown to talent)</span>
                </label>
                <Textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="e.g. The back of your ID is missing. Please upload both sides."
                  rows={3}
                  data-testid="idv-reject-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={!rejectReason || submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="idv-reject-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Reject & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI-IDV — Config dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md" data-testid="ai-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-600" /> AI Reviewer Settings
            </DialogTitle>
          </DialogHeader>
          {!aiConfig ? (
            <div className="text-sm text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <div className="space-y-4 py-2">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={!!aiConfig.enabled}
                  onChange={(e) => saveConfig({ enabled: e.target.checked })}
                  disabled={savingConfig}
                  data-testid="ai-config-enabled"
                />
                <span className="text-sm">
                  <strong>AI reviewer enabled</strong>
                  <div className="text-xs text-slate-500">Uncheck to route every submission to the manual queue.</div>
                </span>
              </label>

              <div>
                <label className="text-sm font-semibold" htmlFor="ai-threshold">
                  Auto-approve threshold: <span className="text-indigo-700">{aiConfig.auto_approve_threshold}%</span>
                </label>
                <input
                  id="ai-threshold"
                  type="range" min="50" max="100" step="1"
                  value={aiConfig.auto_approve_threshold || 95}
                  onChange={(e) => setAiConfig({ ...aiConfig, auto_approve_threshold: Number(e.target.value) })}
                  onMouseUp={(e) => saveConfig({ auto_approve_threshold: Number(e.target.value) })}
                  onTouchEnd={(e) => saveConfig({ auto_approve_threshold: Number(e.target.value) })}
                  className="w-full mt-1"
                  data-testid="ai-config-threshold"
                />
                <div className="text-xs text-slate-500">Submissions scoring at or above this value with no flags will auto-approve.</div>
              </div>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={!!aiConfig.auto_approve_requires_no_flags}
                  onChange={(e) => saveConfig({ auto_approve_requires_no_flags: e.target.checked })}
                  disabled={savingConfig}
                  data-testid="ai-config-require-no-flags"
                />
                <span className="text-sm">
                  <strong>Require zero flags for auto-approve</strong>
                  <div className="text-xs text-slate-500">Recommended — any flag routes to manual review.</div>
                </span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={!!aiConfig.block_on_duplicate}
                  onChange={(e) => saveConfig({ block_on_duplicate: e.target.checked })}
                  disabled={savingConfig}
                  data-testid="ai-config-block-dup"
                />
                <span className="text-sm">
                  <strong>Block auto-approve on duplicate detection</strong>
                  <div className="text-xs text-slate-500">Uses document # + normalized name + DOB signature.</div>
                </span>
              </label>

              <div className="text-xs text-slate-500 pt-2 border-t">
                Model: <code className="text-slate-800">{aiConfig.model_name}</code>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)} data-testid="ai-config-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IdvReviewQueue;
